import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  adminFederatedIdentitiesEndpoints,
  type FederatedIdentityDto,
  type FederatedIdentityStatusDto,
} from './endpoints';

/**
 * Api layer for the global-plane federated-identity admin screen (PTV2-1451). Wire DTOs are all-optional
 * snake_case; the mapper normalises to non-null camelCase view models. Link/unlink mutations share an
 * {@code invalidate} that refreshes every status list so a linked/retired row leaves the PENDING view.
 */

export interface FederatedIdentitiesQuery {
  readonly status?: FederatedIdentityStatusDto;
  readonly userGuid?: string;
}

export const adminFederatedIdentitiesQueryKeys = {
  all: ['admin', 'federated-identities'] as const,
  list: (params: FederatedIdentitiesQuery) =>
    ['admin', 'federated-identities', params.status ?? null, params.userGuid ?? null] as const,
};

export interface FederatedIdentityViewModel {
  readonly guid: string;
  readonly userGuid: string;
  /** Resolved display name of the linked user; empty for a PENDING capture. */
  readonly userName: string;
  readonly providerIssuer: string;
  readonly externalSubject: string;
  readonly status: FederatedIdentityStatusDto;
  readonly capturedEmail: string;
  readonly capturedName: string;
}

export function mapFederatedIdentity(dto: FederatedIdentityDto): FederatedIdentityViewModel {
  return {
    guid: dto.guid ?? '',
    userGuid: dto.user_guid ?? '',
    userName: dto.user_name ?? '',
    providerIssuer: dto.provider_issuer ?? '',
    externalSubject: dto.external_subject ?? '',
    status: dto.status ?? 'PENDING',
    capturedEmail: dto.captured_email ?? '',
    capturedName: dto.captured_name ?? '',
  };
}

/**
 * Captured identities, filtered by lifecycle state (`status`, the admin inbox default) OR by linked
 * user (`userGuid`, for the profile panel / user filter). An empty query object defaults to PENDING
 * server-side. `enabled` lets the profile panel gate on a resolved guid.
 */
export function useFederatedIdentities(
  params: FederatedIdentitiesQuery,
  options?: { enabled?: boolean },
): UseQueryResult<FederatedIdentityViewModel[]> {
  return useQuery<FederatedIdentityViewModel[]>({
    queryKey: adminFederatedIdentitiesQueryKeys.list(params),
    enabled: options?.enabled ?? true,
    queryFn: async ({ signal }) => {
      const list = await adminFederatedIdentitiesEndpoints.getFederatedIdentities(params, { signal });
      return list.map(mapFederatedIdentity);
    },
  });
}

export interface LinkFederatedIdentityInput {
  readonly guid: string;
  readonly userGuid: string;
}

export function useFederatedIdentityMutations(): {
  readonly link: UseMutationResult<FederatedIdentityViewModel, Error, LinkFederatedIdentityInput>;
  readonly unlink: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();
  // Any link/unlink can move a row between status lists, so refresh the whole family.
  const invalidate = (): void => {
    queryClient
      .invalidateQueries({ queryKey: adminFederatedIdentitiesQueryKeys.all })
      .catch(() => undefined);
  };

  const link = useMutation<FederatedIdentityViewModel, Error, LinkFederatedIdentityInput>({
    mutationFn: async ({ guid, userGuid }) =>
      mapFederatedIdentity(
        await adminFederatedIdentitiesEndpoints.linkFederatedIdentity(guid, { user_guid: userGuid }),
      ),
    onSettled: invalidate,
  });

  const unlink = useMutation<undefined, Error, string>({
    mutationFn: (guid) => adminFederatedIdentitiesEndpoints.unlinkFederatedIdentity(guid),
    onSettled: invalidate,
  });

  return { link, unlink };
}
